/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { useRef, useEffect } from "react";
import { getTasksListsForUser, getTasksListForUser, getCompletedTasksForUser, updateTask, getAccounts, insertTask, createTasklist, deleteTasklist, deleteTask, updateAccount, disconnectAccount, moveTask, checkTaskFusionCalendar, createTaskFusionCalendar, getTaskFusionCalendar, getCalendarEvents, getCalendarEvent, createEventFromTask, deleteCalendarEvent, updateCalendarEvent, updateCalendarEventTime, listCalendars, getTaskEventMappings, deleteTaskEventMapping } from "@/api";
import { getAllTaskIdsFromCache, hasAccountTasksLoaded } from "@/utils/cleanupHelpers";
import { IAccount } from "@/types";
import { tasks_v1 } from "@googleapis/tasks";
import { calendar_v3 } from "@googleapis/calendar";
import { useMutation, useQueries, useQueryClient, useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { updateTaskPositions, sortTasksByMoveParams } from "@/helpers";
import { ExtendedMoveTaskParams } from "@/components/Task";
import { useConfigCleanup } from "./useConfig";
import { useAnalytics } from "./useAnalytics";

// const setDiff = (a: Set<string>, b: Set<string>) => new Set([...a].filter((x) => !b.has(x)));


export const useTitleMap = () => {
  const queryClient = useQueryClient();

  const myMap: Record<string, string> = {};
  queryClient.getQueriesData({ queryKey: ["tasklists"]}).forEach(([queryKey, value]) => {
    if (queryKey[1].userId) {
      value?.forEach(val => myMap[val.id] = val.title);
    }
    return null;
  })
  return myMap;
}

export const useAccounts = () => useQuery({
  queryKey: ["accounts"],
  queryFn: getAccounts,
  retry: (failureCount, error: any) => {
    // Don't retry on auth errors (401/403)
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      return false;
    }
    return failureCount < 3;
  },
  retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
})

export const useDisconnectAccount = () => {
  const queryClient = useQueryClient();
  const mutationKey = ["accounts"];
  const { cleanupAccount } = useConfigCleanup();
  const analytics = useAnalytics();

  return useMutation({
    mutationFn: async (variables: { account: IAccount }) =>
      disconnectAccount(variables.account),
    onMutate: async ({ account }) => {
      await queryClient.cancelQueries({ queryKey: mutationKey });
      const previousAccounts = queryClient.getQueryData(mutationKey);

      queryClient.setQueryData(mutationKey, (old: Record<string, IAccount>) => {
        const newAccounts = { ...old };
        delete newAccounts[account.id];
        return newAccounts;
      });

      cleanupAccount(account.id);

      return { previousAccounts };
    },
    onError: (err, _, context) => {
      console.error('Error disconnecting account:', err);
      queryClient.setQueryData(mutationKey, context.previousAccounts);
    },
    onSettled: async (_, error, variables) => {
      if (error) {
        throw new Error(error);
      } else {
        await queryClient.invalidateQueries({ queryKey: mutationKey });

        const currentAccounts = queryClient.getQueryData<Record<string, IAccount>>(mutationKey);
        const newCount = currentAccounts ? Object.keys(currentAccounts).length : 0;
        analytics.track("account_disconnected", {
          disconnected_account_email: variables.account.email,
        });
        analytics.setPersonProps({ accounts_connected: newCount });
        analytics.updateSuperProperties({ accounts_connected: newCount });
      }
    },
  });
};

export const useUpdateAccount = () => {
  const queryClient = useQueryClient();
  const mutationKey = ["accounts"];

  return useMutation({
    mutationFn: async (variables: { account: IAccount; updatedFields: Partial<IAccount> }) =>
      updateAccount(variables.account, variables.updatedFields),
    onMutate: async ({ account, updatedFields }) => {
      await queryClient.cancelQueries({ queryKey: mutationKey });
      const previousAccounts = queryClient.getQueryData(mutationKey);

      queryClient.setQueryData(mutationKey, (old: Record<string, IAccount>) => ({
        ...old,
        [account.id]: { ...old[account.id], ...updatedFields }
      }));

      return { previousAccounts };
    },
    onError: (err, _, context) => {
      console.error('Error updating account:', err);
      queryClient.setQueryData(mutationKey, context.previousAccounts);
    },
    onSettled: async (_, error) => {
      if (error) {
        throw new Error(error);
      } else {
        await queryClient.invalidateQueries({ queryKey: mutationKey });
      }
    },
  });
};

export const useTasklists = (accounts: { [id: string]: IAccount } | undefined) =>
  useQueries({
    queries: accounts && Object.keys(accounts).length
      ? Object.entries(accounts).map(([id, account]) => ({
          queryKey: ["tasklists", { userId: id }],
          queryFn: () => getTasksListsForUser(account),
        }))
      : [],
  });

export const useTaskList = (account: IAccount, listId: string) => useQuery({
  queryKey: ["tasklist", { userId: account.id, tasklistId: listId }],
  queryFn: () => getTasksListForUser(account, listId),
  refetchOnWindowFocus: true,
});

export const useCompletedTaskList = (account: IAccount, listId: string) =>
  useInfiniteQuery({
    queryKey: ["completed-tasklist", { userId: account.id, tasklistId: listId }],
    queryFn: ({ pageParam }) => getCompletedTasksForUser(account, listId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

export const useAllTaskListsForUser = (accounts: { [id: string]: IAccount } | undefined) => {
  const tasklistsQueries = useTasklists(accounts);
  const allListsQueryResults = useQueries({
    queries: accounts && tasklistsQueries.length ? tasklistsQueries.filter(q => !q.isLoading).map(q => q.data).flat().map((list) => ({
      queryKey: ["tasklist", { userId: list?.accountId, tasklistId: list?.id }],
      queryFn: () => getTasksListForUser(accounts[list?.accountId || ""], list?.id || "")
    })) : [],
    combine: (results) => {
      const obj: Record<string, object> = Object.keys(accounts || {}).reduce((prev, curr) => {
        return {
          ...prev,
          [curr]: {}
        }
      }, {});
      results.forEach(res => {
        if (!res.isLoading) {
          if (res.data?.length) {
            const accountId = res.data[0]?.accountId;
            const listId = res.data[0]?.tasklistId;
            obj[accountId][listId] = res?.data;
          }
        }
      })
      return obj;
    }
  })

  return allListsQueryResults;
};

const printDebugList = (list: ExtendedTask[], label: string) => {
  // console.log(label, list.map(task => ({ title: task.title, pos: task.position })))
  return null;
}

export const useUpdateTask = (account: IAccount, tasklist: string) => {
  const queryClient = useQueryClient();
  const mutationKey = ["tasklist", { userId: account.id, tasklistId: tasklist }];

  return useMutation({
    mutationFn: async (variables: { task: string; updatedFields: Partial<tasks_v1.Schema$Task> }) =>
      updateTask(account, tasklist, variables.task, variables.updatedFields),
    onSettled: async (_, error) => {
      if (error) console.error(error);
      else {
        await queryClient.invalidateQueries({ queryKey: mutationKey });
      }
    },
    onMutate: async ({ task: newTaskId, updatedFields }) => {
      await queryClient.cancelQueries({ queryKey: mutationKey })
      const previousList = queryClient.getQueryData(mutationKey);
      queryClient.setQueryData(mutationKey, (old) => {
        const updatedList = old.map((task) => {
          const updatedTask = task.id === newTaskId ? { ...task, ...updatedFields } : task;
          const res = task.id !== newTaskId ? task : updatedTask;
          return res;
        })
        // printDebugList(updatedList, "updatedList")
        return updatedList;
      })
      return { previousList }
    },
    onError: (err, newList, context) => {
      console.log('err', err)
      queryClient.setQueryData(mutationKey, context.previousList)
    },
  });
};

export const useCompleteTask = (account: IAccount, tasklist: string) => {
  const queryClient = useQueryClient();
  const mutationKey = ["tasklist", { userId: account.id, tasklistId: tasklist }];
  const completedQueryKey = ["completed-tasklist", { userId: account.id, tasklistId: tasklist }];

  return useMutation({
    mutationFn: async (variables: { task: string; updatedFields: Partial<tasks_v1.Schema$Task> }) =>
      updateTask(account, tasklist, variables.task, variables.updatedFields),
    onSettled: async (_, error) => {
      if (error) console.error(error);
      else {
        await queryClient.invalidateQueries({ queryKey: mutationKey });
        await queryClient.invalidateQueries({ queryKey: completedQueryKey });
      }
    },
    onMutate: async ({ task: newTaskId, updatedFields }) => {
      await queryClient.cancelQueries({ queryKey: mutationKey });
      await queryClient.cancelQueries({ queryKey: completedQueryKey });

      const previousList = queryClient.getQueryData(mutationKey);
      const previousCompleted = queryClient.getQueryData(completedQueryKey);
      const isCompleting = updatedFields.status === "completed";

      if (isCompleting) {
        const taskToComplete = previousList?.find((t) => t.id === newTaskId);
        const completedTask = taskToComplete
          ? { ...taskToComplete, ...updatedFields, tasklistId: tasklist, accountId: account.id }
          : null;

        queryClient.setQueryData(mutationKey, (old) => old.filter((t) => t.id !== newTaskId));

        if (completedTask) {
          queryClient.setQueryData(completedQueryKey, (old) => {
            if (!old) {
              return {
                pages: [{ items: [completedTask], nextPageToken: null }],
                pageParams: [undefined],
              };
            }
            const [firstPage, ...restPages] = old.pages;
            return {
              ...old,
              pages: [{ ...firstPage, items: [completedTask, ...firstPage.items] }, ...restPages],
            };
          });
        }
      } else {
        const completedData = queryClient.getQueryData(completedQueryKey) as { pages: { items: unknown[] }[] } | undefined;
        const taskToUncomplete = completedData?.pages?.flatMap((p) => p.items).find((t: { id?: string }) => t.id === newTaskId);
        const activeTask = taskToUncomplete
          ? { ...taskToUncomplete, ...updatedFields, tasklistId: tasklist, accountId: account.id }
          : null;

        queryClient.setQueryData(completedQueryKey, (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.filter((t: { id?: string }) => t.id !== newTaskId),
            })),
          };
        });

        if (activeTask) {
          queryClient.setQueryData(mutationKey, (old) => [activeTask, ...(old || [])]);
        }
      }

      return { previousList, previousCompleted };
    },
    onError: (err, _, context) => {
      console.log('err', err);
      queryClient.setQueryData(mutationKey, context.previousList);
      if (context.previousCompleted !== undefined) {
        queryClient.setQueryData(completedQueryKey, context.previousCompleted);
      }
    },
  });
};

export const useInsertTask = (account: IAccount, tasklist: string) => {
  const queryClient = useQueryClient();
  const mutationKey = ["tasklist", { userId: account.id, tasklistId: tasklist }];

  return useMutation({
    mutationFn: async (variables: { tasklist: string; task: Partial<tasks_v1.Schema$Task> }) =>
      insertTask(account, tasklist, variables.task),
    onMutate: async ({ tasklist, task }) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      const queryKey = ["tasklist", { userId: account.id, tasklistId: tasklist }];
      await queryClient.cancelQueries({ queryKey })
  
      // Snapshot the previous value
      const previousList = queryClient.getQueryData(queryKey)
  
      // Optimistically update to the new value
      const newQueryData = [ { tasklistId: tasklist, ...task, status: "needsAction", id: "temp-id" }, ...previousList]
      queryClient.setQueryData(queryKey, newQueryData)
  
      // Return a context object with the snapshotted value
      return { previousList }
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, newList, context) => {
      console.log('err', err)
      const queryKey = ["tasklist", { userId: account.id, tasklistId: tasklist }];
      queryClient.setQueryData(queryKey, context.previousList)
    },
    onSettled: async (_, error) => {
      if (error) {
        console.error(error);
        throw new Error(error);
      }
      else {
        return await queryClient.invalidateQueries({ queryKey: mutationKey });
      }
    },
  });
};

export const useInsertTasklist = (account: IAccount) => {
  const queryClient = useQueryClient();
  const queryKey = ["tasklists", { userId: account.id }];

  return useMutation({
    mutationKey: queryKey,
    mutationFn: async (variables: { account: IAccount; title: string }) =>
      createTasklist(variables.account, variables.title),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });
      
      // Snapshot the previous value
      const previousLists = queryClient.getQueryData(queryKey);
      
      // Create an optimistic tasklist with a temporary ID
      const tempId = `temp-${Date.now()}`;
      const optimisticTasklist = {
        id: tempId,
        title: variables.title,
        accountId: account.id,
        kind: "tasks#taskList"
      };

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, (old = []) => [...old, optimisticTasklist]);
      
      // Return the context with the temp ID to use in onSuccess
      return { 
        previousLists,
        tempId
      };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, newList, context) => {
      console.log('err', err);
      queryClient.setQueryData(queryKey, context?.previousLists);
    },
    onSettled: async (_, error, variables) => {
      if (error) {
        console.error(error);
        throw new Error(error);
      }
      else {
        return await queryClient.invalidateQueries({ queryKey });
      }
    },
  });
};

export const useDeleteTasklist = (account: IAccount) => {
  const queryClient = useQueryClient();
  const queryKey = ["tasklists", { userId: account.id }];
  const { cleanupTasklist } = useConfigCleanup();

  return useMutation({
    mutationKey: queryKey,
    mutationFn: async (variables: { account: IAccount; tasklistId: string }) => {
      return deleteTasklist(variables.account, variables.tasklistId);
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });
  
      // Snapshot the previous value
      const previousLists = queryClient.getQueryData(queryKey);
  
      // Optimistically update to the new value by removing the list
      queryClient.setQueryData(queryKey, (old = []) => 
        old.filter((list) => list.id !== variables.tasklistId)
      );
  
      // Return a context object with the snapshotted value
      return { previousLists };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, _, context) => {
      console.error('Error deleting tasklist:', err);
      queryClient.setQueryData(queryKey, context?.previousLists);
    },
    onSettled: async (_, error, variables) => {
      if (error) {
        console.error(error);
        throw new Error(error);
      }
      else {
        // Only cleanup config after successful deletion
        cleanupTasklist(account.id, variables.tasklistId);
        return await queryClient.invalidateQueries({ queryKey });
      }
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { account: IAccount; tasklist: string; task: string }) =>
      deleteTask(variables.account, variables.tasklist, variables.task),
    onMutate: async ({ account, tasklist, task }) => {
      const queryKey = ["tasklist", { userId: account.id, tasklistId: tasklist }];
      const completedQueryKey = ["completed-tasklist", { userId: account.id, tasklistId: tasklist }];

      await queryClient.cancelQueries({ queryKey });
      await queryClient.cancelQueries({ queryKey: completedQueryKey });

      const previousList = queryClient.getQueryData(queryKey);
      const previousCompleted = queryClient.getQueryData(completedQueryKey);

      queryClient.setQueryData(queryKey, (old) => old.filter(t => t.id !== task));

      queryClient.setQueryData(completedQueryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter((t: { id?: string }) => t.id !== task),
          })),
        };
      });

      return { previousList, previousCompleted };
    },
    onError: (err, variables, context) => {
      const queryKey = ["tasklist", { userId: variables.account.id, tasklistId: variables.tasklist }];
      const completedQueryKey = ["completed-tasklist", { userId: variables.account.id, tasklistId: variables.tasklist }];
      queryClient.setQueryData(queryKey, context?.previousList);
      if (context?.previousCompleted !== undefined) {
        queryClient.setQueryData(completedQueryKey, context.previousCompleted);
      }
    },
    onSettled: async (_, error, variables) => {
      if (!error) {
        const queryKey = ["tasklist", { userId: variables.account.id, tasklistId: variables.tasklist }];
        const completedQueryKey = ["completed-tasklist", { userId: variables.account.id, tasklistId: variables.tasklist }];
        await queryClient.invalidateQueries({ queryKey });
        await queryClient.invalidateQueries({ queryKey: completedQueryKey });
      }
    },
  });
};

export const useMoveTaskLegacy = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (variables: {
      sourceAccount: IAccount;
      sourceTasklist: string;
      targetAccount: IAccount;
      targetTasklist: string;
      task: tasks_v1.Schema$Task;
    }) => {
      // First create the task in the target list
      const newTask = await insertTask(variables.targetAccount, variables.targetTasklist, {
        title: variables.task.title,
        notes: variables.task.notes,
        due: variables.task.due,
        status: variables.task.status,
      });
      
      // Then delete from the source list
      await deleteTask(variables.sourceAccount, variables.sourceTasklist, variables.task.id);
      
      return newTask;
    },
    onMutate: async ({ sourceAccount, sourceTasklist, targetAccount, targetTasklist, task }) => {
      const sourceQueryKey = ["tasklist", { userId: sourceAccount.id, tasklistId: sourceTasklist }];
      const targetQueryKey = ["tasklist", { userId: targetAccount.id, tasklistId: targetTasklist }];
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: sourceQueryKey });
      await queryClient.cancelQueries({ queryKey: targetQueryKey });
      
      // Snapshot the previous values
      const previousSourceList = queryClient.getQueryData(sourceQueryKey);
      const previousTargetList = queryClient.getQueryData(targetQueryKey);
      
      // Optimistically update both lists
      queryClient.setQueryData(sourceQueryKey, (old) => 
        old.filter(t => t.id !== task.id)
      );
      
      queryClient.setQueryData(targetQueryKey, (old) => [
        { ...task, tasklistId: targetTasklist },
        ...old
      ]);
      
      return { previousSourceList, previousTargetList };
    },
    onError: (err, variables, context) => {
      // Revert both lists on error
      const sourceQueryKey = ["tasklist", { userId: variables.sourceAccount.id, tasklistId: variables.sourceTasklist }];
      const targetQueryKey = ["tasklist", { userId: variables.targetAccount.id, tasklistId: variables.targetTasklist }];
      
      queryClient.setQueryData(sourceQueryKey, context.previousSourceList);
      queryClient.setQueryData(targetQueryKey, context.previousTargetList);
      
      console.error('Error moving task:', err);
    },
    onSettled: async (_, error, variables) => {
      if (error) {
        throw new Error(error);
      } else {
        // Invalidate both affected lists
        await queryClient.invalidateQueries({
          queryKey: ["tasklist", { userId: variables.sourceAccount.id, tasklistId: variables.sourceTasklist }]
        });
        await queryClient.invalidateQueries({
          queryKey: ["tasklist", { userId: variables.targetAccount.id, tasklistId: variables.targetTasklist }]
        });
        
        // Invalidate task-event-mappings for both accounts to trigger calendar cleanup
        await queryClient.invalidateQueries({
          queryKey: ["task-event-mappings", { accountId: variables.sourceAccount.id }]
        });
        if (variables.sourceAccount.id !== variables.targetAccount.id) {
          await queryClient.invalidateQueries({
            queryKey: ["task-event-mappings", { accountId: variables.targetAccount.id }]
          });
        }
        
        // Invalidate calendar events to refresh calendar view
        await queryClient.invalidateQueries({
          queryKey: ["calendar-events"]
        });
      }
    },
  });
};

export const useMoveTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (variables: ExtendedMoveTaskParams & { updatedSourceList?: ExtendedTask[], updatedDestinationList?: ExtendedTask[] }) => {
      const { account, tasklist, taskId, parent, previous, destinationTasklist } = variables;
      return moveTask(account, tasklist, taskId, { parent, previous, destinationTasklist });
    },
    onMutate: async ({ account, tasklist, taskId, parent, previous, destinationTasklist, updatedSourceList, updatedDestinationList }) => {
      // Define query keys for affected tasklists
      const sourceListKey = ["tasklist", { userId: account.id, tasklistId: tasklist }];
      
      // If destination list is specified and different from source, we need to track both
      const isMovingBetweenLists = destinationTasklist && (destinationTasklist !== tasklist);
      const destinationListKey = isMovingBetweenLists 
        ? ["tasklist", { userId: account.id, tasklistId: destinationTasklist }]
        : null;
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: sourceListKey });
      if (destinationListKey) {
        await queryClient.cancelQueries({ queryKey: destinationListKey });
      }
      
      // Snapshot the previous values
      const previousSourceList = queryClient.getQueryData(sourceListKey);
      const previousDestinationList = destinationListKey 
        ? queryClient.getQueryData(destinationListKey)
        : null;
      
      // Find the task being moved
      const taskToMove = previousSourceList?.find(t => t.id === taskId);

      if (isMovingBetweenLists) {
        queryClient.setQueryData(sourceListKey, (old) => {
          const filteredSourceList = old.filter(t => t.id !== taskId);
          return updateTaskPositions(filteredSourceList);
        });
        queryClient.setQueryData(destinationListKey, (old) => {
          const completed = old.filter(t => t.status === "completed");
          /** we are going to be updating the client side state of the list so we need the completed tasks to be included
           * These are filtered out when storing the ui state in the store so that we can easily perform reordering
           */
          const optimisticDestinationList = [...updatedDestinationList, ...completed];
          const withUpdatedPostions = updateTaskPositions(optimisticDestinationList);
          return withUpdatedPostions;
        });
      } else {
        queryClient.setQueryData(sourceListKey, (old) => {
          const sortedOldList = [...old].sort((a, b) => a.position.localeCompare(b.position));
          // printDebugList(sortedOldList, "sorted old list")
          let updatedList = sortTasksByMoveParams(sortedOldList, taskId, { parent, previous, destinationTasklist });
          updatedList = updateTaskPositions(updatedList);
          // printDebugList(updatedList, "optimistic update")
          return updatedList;
        });
      }
      
      return { 
        previousSourceList, 
        previousDestinationList,
        sourceListKey,
        destinationListKey 
      };
    },
    onError: (err, variables, context) => {
      // Revert changes on error
      queryClient.setQueryData(context.sourceListKey, context.previousSourceList);
      
      if (context.destinationListKey) {
        queryClient.setQueryData(context.destinationListKey, context.previousDestinationList);
      }
      
      console.error('Error moving task:', err);
    },
    onSettled: async (_, error, variables) => {
      if (error) {
        console.error(error);
      }
      
      // Invalidate source list to refresh data
      const sourceListKey = ["tasklist", { userId: variables.account.id, tasklistId: variables.tasklist }];
      await queryClient.invalidateQueries({ queryKey: sourceListKey });
      
      // If destination list is different, invalidate it too
      if (variables.destinationTasklist && (variables.destinationTasklist !== variables.tasklist)) {
        const destinationListKey = ["tasklist", { 
          userId: variables.account.id, 
          tasklistId: variables.destinationTasklist 
        }];
        await queryClient.invalidateQueries({ queryKey: destinationListKey });
      }
    },
  });
};

export const useTaskFusionCalendar = (accountId: string, userId: string) => {
  return useQuery({
    queryKey: ["task-fusion-calendar", { accountId }],
    queryFn: () => getTaskFusionCalendar(accountId, userId),
    retry: false,
    enabled: !!accountId && !!userId,
  });
};

export const useCalendarEvents = (accountId: string, calendarId: string, userId: string, timeRange?: { timeMin?: string; timeMax?: string }) => {
  return useQuery({
    queryKey: ["calendar-events", { accountId, calendarId, timeRange }],
    queryFn: () => getCalendarEvents(accountId, calendarId, userId, timeRange?.timeMin, timeRange?.timeMax),
    enabled: !!accountId && !!calendarId && !!userId,
  });
};

export const useCreateTaskFusionCalendar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accountId, userId }: { accountId: string; userId: string }) =>
      createTaskFusionCalendar(accountId, userId),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ["task-fusion-calendar", { accountId: variables.accountId }],
        data
      );
    },
  });
};

export const useCreateEventFromTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accountId, calendarId, task, userId }: { accountId: string; calendarId: string; task: any; userId: string }) =>
      createEventFromTask(accountId, calendarId, task, userId),
    onMutate: async ({ accountId, calendarId, task, userId }) => {
      // Cancel queries that match by accountId and calendarId (ignore timeRange)
      await queryClient.cancelQueries({
        predicate: (query) =>
          query.queryKey[0] === "calendar-events" &&
          (query.queryKey[1] as any)?.accountId === accountId &&
          (query.queryKey[1] as any)?.calendarId === calendarId
      });
      
      // Get all matching queries (with different timeRanges)
      const matchingQueries = queryClient.getQueriesData<calendar_v3.Schema$Event[]>({
        predicate: (query) =>
          query.queryKey[0] === "calendar-events" &&
          (query.queryKey[1] as any)?.accountId === accountId &&
          (query.queryKey[1] as any)?.calendarId === calendarId
      });
      
      const previousEventsMap = new Map(matchingQueries);
      
      // Use task.startTime if provided (drag-and-drop), otherwise default to 9am
      let startDateTime: string;
      if (task.startTime) {
        startDateTime = task.startTime;
      } else if (task.due) {
        startDateTime = new Date(task.due).toISOString().split('T')[0] + 'T09:00:00Z';
      } else {
        startDateTime = new Date().toISOString().split('T')[0] + 'T09:00:00Z';
      }
      
      const endDateTime = new Date(new Date(startDateTime).getTime() + 30 * 60 * 1000).toISOString();
      
      const optimisticEvent = {
        id: "temp-id",
        summary: task.title,
        description: task.notes || "",
        start: {
          dateTime: startDateTime,
        },
        end: {
          dateTime: endDateTime,
        },
      };
      
      // Add optimistic event to all matching queries
      matchingQueries.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old: calendar_v3.Schema$Event[] = []) => [...old, optimisticEvent]);
      });
      
      return { previousEventsMap, accountId, calendarId };
    },
    onSuccess: (data, variables, context) => {
      // Replace temp event with real event from response (no flicker)
      const matchingQueries = queryClient.getQueriesData<calendar_v3.Schema$Event[]>({
        predicate: (query) =>
          query.queryKey[0] === "calendar-events" &&
          (query.queryKey[1] as any)?.accountId === variables.accountId &&
          (query.queryKey[1] as any)?.calendarId === variables.calendarId
      });
      
      matchingQueries.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old: calendar_v3.Schema$Event[] = []) =>
          old.map(event => event.id === "temp-id" ? data : event)
        );
      });
      
      // Invalidate mappings to update the scheduled badge
      queryClient.invalidateQueries({
        queryKey: ["task-event-mappings", { accountId: variables.accountId }],
      });
    },
    onError: (err, variables, context) => {
      // Revert all affected queries
      if (context?.previousEventsMap) {
        context.previousEventsMap.forEach((events, queryKey) => {
          queryClient.setQueryData(queryKey, events);
        });
      }
      console.error('Error creating event:', err);
    },
  });
};

export const useDeleteCalendarEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accountId, calendarId, eventId, userId }: { accountId: string; calendarId: string; eventId: string; userId: string }) =>
      deleteCalendarEvent(accountId, calendarId, eventId, userId),
    onMutate: async ({ accountId, calendarId, eventId }) => {
      await queryClient.cancelQueries({
        predicate: (query) =>
          query.queryKey[0] === "calendar-events" &&
          (query.queryKey[1] as any)?.accountId === accountId &&
          (query.queryKey[1] as any)?.calendarId === calendarId
      });
      
      const matchingQueries = queryClient.getQueriesData<calendar_v3.Schema$Event[]>({
        predicate: (query) =>
          query.queryKey[0] === "calendar-events" &&
          (query.queryKey[1] as any)?.accountId === accountId &&
          (query.queryKey[1] as any)?.calendarId === calendarId
      });
      
      const previousEventsMap = new Map(matchingQueries);
      
      matchingQueries.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old: calendar_v3.Schema$Event[] = []) =>
          old.filter(event => event.id !== eventId)
        );
      });
      
      return { previousEventsMap };
    },
    onError: (err, variables, context) => {
      if (context?.previousEventsMap) {
        context.previousEventsMap.forEach((events, queryKey) => {
          queryClient.setQueryData(queryKey, events);
        });
      }
      console.error('Error deleting event:', err);
    },
    onSettled: async (_, error, variables) => {
      if (!error) {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "calendar-events" &&
            (query.queryKey[1] as any)?.accountId === variables.accountId &&
            (query.queryKey[1] as any)?.calendarId === variables.calendarId
        });
      }
    },
  });
};

export const useUpdateCalendarEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accountId, calendarId, eventId, task, userId }: { 
      accountId: string; 
      calendarId: string; 
      eventId: string; 
      task: any; 
      userId: string 
    }) => updateCalendarEvent(accountId, calendarId, eventId, task, userId),
    onMutate: async ({ accountId, calendarId, eventId, task }) => {
      await queryClient.cancelQueries({ 
        predicate: (query) => 
          query.queryKey[0] === "calendar-events" && 
          (query.queryKey[1] as any)?.accountId === accountId &&
          (query.queryKey[1] as any)?.calendarId === calendarId
      });
      
      const matchingQueries = queryClient.getQueriesData<calendar_v3.Schema$Event[]>({
        predicate: (query) => 
          query.queryKey[0] === "calendar-events" && 
          (query.queryKey[1] as any)?.accountId === accountId &&
          (query.queryKey[1] as any)?.calendarId === calendarId
      });
      
      const previousEventsMap = new Map(matchingQueries);
      
      matchingQueries.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old: calendar_v3.Schema$Event[] = []) =>
          old.map(event => {
            if (event.id !== eventId) return event;
            
            const updated = { ...event };
            if (task.title !== undefined) updated.summary = task.title;
            if (task.notes !== undefined) updated.description = task.notes;
            if (task.due !== undefined) {
              const newDate = new Date(task.due);
              
              // Preserve existing time if event has one
              if (event.start?.dateTime) {
                const existingStart = new Date(event.start.dateTime);
                const existingEnd = event.end?.dateTime ? new Date(event.end.dateTime) : null;
                const duration = existingEnd 
                  ? existingEnd.getTime() - existingStart.getTime() 
                  : 30 * 60 * 1000;
                
                const startDate = new Date(newDate);
                startDate.setUTCHours(existingStart.getUTCHours(), existingStart.getUTCMinutes(), 0, 0);
                const endDate = new Date(startDate.getTime() + duration);
                
                updated.start = { dateTime: startDate.toISOString() };
                updated.end = { dateTime: endDate.toISOString() };
              } else {
                // No existing time, default to 9:00 AM
                const startDate = new Date(newDate);
                startDate.setUTCHours(9, 0, 0, 0);
                const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);
                updated.start = { dateTime: startDate.toISOString() };
                updated.end = { dateTime: endDate.toISOString() };
              }
            }
            return updated;
          })
        );
      });
      
      return { previousEventsMap };
    },
    onError: (err, variables, context) => {
      if (context?.previousEventsMap) {
        context.previousEventsMap.forEach((events, queryKey) => {
          queryClient.setQueryData(queryKey, events);
        });
      }
      console.error('Error updating calendar event:', err);
    },
    onSettled: async (_, error, variables) => {
      if (!error) {
        await queryClient.invalidateQueries({
          predicate: (query) => 
            query.queryKey[0] === "calendar-events" && 
            (query.queryKey[1] as any)?.accountId === variables.accountId &&
            (query.queryKey[1] as any)?.calendarId === variables.calendarId
        });
      }
    },
  });
};

export const useUpdateCalendarEventTime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accountId, calendarId, eventId, startTime, endTime, userId }: { 
      accountId: string; 
      calendarId: string; 
      eventId: string; 
      startTime: string;
      endTime: string;
      userId: string 
    }) => updateCalendarEventTime(accountId, calendarId, eventId, startTime, endTime, userId),
    onMutate: async ({ accountId, calendarId, eventId, startTime, endTime }) => {
      await queryClient.cancelQueries({ 
        predicate: (query) => 
          query.queryKey[0] === "calendar-events" && 
          (query.queryKey[1] as any)?.accountId === accountId &&
          (query.queryKey[1] as any)?.calendarId === calendarId
      });
      
      const matchingQueries = queryClient.getQueriesData<calendar_v3.Schema$Event[]>({
        predicate: (query) => 
          query.queryKey[0] === "calendar-events" && 
          (query.queryKey[1] as any)?.accountId === accountId &&
          (query.queryKey[1] as any)?.calendarId === calendarId
      });
      
      const previousEventsMap = new Map(matchingQueries);
      
      matchingQueries.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old: calendar_v3.Schema$Event[] = []) =>
          old.map(event => {
            if (event.id !== eventId) return event;
            return {
              ...event,
              start: { dateTime: startTime },
              end: { dateTime: endTime },
            };
          })
        );
      });
      
      return { previousEventsMap };
    },
    onError: (err, variables, context) => {
      if (context?.previousEventsMap) {
        context.previousEventsMap.forEach((events, queryKey) => {
          queryClient.setQueryData(queryKey, events);
        });
      }
      console.error('Error updating calendar event time:', err);
    },
    onSuccess: (updatedEvent, variables) => {
      // Update cache with server response for just this event (no refetch)
      // This prevents race conditions where a refetch overwrites other events' optimistic updates
      const matchingQueries = queryClient.getQueriesData<calendar_v3.Schema$Event[]>({
        predicate: (query) =>
          query.queryKey[0] === "calendar-events" &&
          (query.queryKey[1] as any)?.accountId === variables.accountId &&
          (query.queryKey[1] as any)?.calendarId === variables.calendarId
      });

      matchingQueries.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old: calendar_v3.Schema$Event[] = []) =>
          old.map(event => event.id === variables.eventId ? updatedEvent : event)
        );
      });
    },
    onSettled: async (_, error, variables) => {
      if (!error) {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "calendar-events" &&
            (query.queryKey[1] as any)?.accountId === variables.accountId &&
            (query.queryKey[1] as any)?.calendarId === variables.calendarId
        });
      }
    },
  });
};

export const useTaskEventMappings = (accountId: string, userId: string) => {
  return useQuery({
    queryKey: ["task-event-mappings", { accountId }],
    queryFn: () => getTaskEventMappings(accountId, userId),
    enabled: !!accountId && !!userId,
  });
};

export const useDeleteTaskEventMapping = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accountId, taskId, userId }: { accountId: string; taskId: string; userId: string }) =>
      deleteTaskEventMapping(accountId, taskId, userId),
    onMutate: async ({ accountId, taskId }) => {
      const queryKey = ["task-event-mappings", { accountId }];
      await queryClient.cancelQueries({ queryKey });
      
      const previousMappings = queryClient.getQueryData(queryKey);
      
      queryClient.setQueryData(queryKey, (old: Record<string, any> = {}) => {
        const newMappings = { ...old };
        delete newMappings[taskId];
        return newMappings;
      });
      
      return { previousMappings };
    },
    onError: (err, variables, context) => {
      const queryKey = ["task-event-mappings", { accountId: variables.accountId }];
      queryClient.setQueryData(queryKey, context?.previousMappings);
      console.error('Error deleting mapping:', err);
    },
    onSettled: async (_, error, variables) => {
      if (!error) {
        await queryClient.invalidateQueries({
          queryKey: ["task-event-mappings", { accountId: variables.accountId }],
        });
        await queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "calendar-events" &&
            (query.queryKey[1] as any)?.accountId === variables.accountId
        });
      }
    },
  });
};

export const useVerifyCalendarEvent = (accountId: string, calendarId: string, eventId: string, userId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["calendar-event", { accountId, calendarId, eventId }],
    queryFn: () => getCalendarEvent(accountId, calendarId, eventId, userId),
    enabled: enabled && !!accountId && !!calendarId && !!eventId && !!userId,
    retry: false,
    // Treat 404 as valid response (event doesn't exist)
    throwOnError: false,
  });
};

export const useCleanupOrphanedMapping = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accountId, taskId, userId }: { accountId: string; taskId: string; userId: string }) =>
      deleteTaskEventMapping(accountId, taskId, userId),
    onSuccess: async (_, variables) => {
      // Invalidate mappings query to update UI
      await queryClient.invalidateQueries({
        queryKey: ["task-event-mappings", { accountId: variables.accountId }],
      });
    },
  });
};

/**
 * Hook to cleanup orphaned events - events whose corresponding tasks no longer exist.
 * Runs when calendar view opens. Deletes both the event and the mapping.
 */
export const useOrphanedEventCleanup = (
  accountIds: string[],
  enabled: boolean = true
) => {
  const queryClient = useQueryClient();
  const deleteEventMutation = useDeleteCalendarEvent();
  const deleteMappingMutation = useDeleteTaskEventMapping();
  const cleanupInProgressRef = useRef(false);

  // Fetch mappings for all accounts
  const mappingsQueries = useQueries({
    queries: accountIds.map(accountId => ({
      queryKey: ["task-event-mappings", { accountId }],
      queryFn: () => getTaskEventMappings(accountId, accountId),
      enabled: enabled && !!accountId,
    })),
  });

  useEffect(() => {
    if (!enabled) return;
    if (cleanupInProgressRef.current) return;

    // Wait for all mappings to load
    const allLoaded = mappingsQueries.every(q => !q.isLoading);
    if (!allLoaded) return;

    // Get all task IDs from cache
    const allTaskIds = getAllTaskIdsFromCache(accountIds, queryClient);
    if (allTaskIds.size === 0) return;

    // Collect all orphaned mappings first
    const orphanedMappings: Array<{ accountId: string; mapping: { taskId: string; calendarId: string; eventId: string } }> = [];

    mappingsQueries.forEach((query, index) => {
      const accountId = accountIds[index];
      const mappings = query.data;
      if (!mappings || !hasAccountTasksLoaded(accountId, queryClient)) return;

      Object.values(mappings).forEach((mapping: { taskId: string; calendarId: string; eventId: string }) => {
        if (!allTaskIds.has(mapping.taskId)) {
          orphanedMappings.push({ accountId, mapping });
        }
      });
    });

    if (orphanedMappings.length === 0) return;

    // Process deletions sequentially to avoid request storms
    cleanupInProgressRef.current = true;
    
    const processOrphanedMappings = async () => {
      for (const { accountId, mapping } of orphanedMappings) {
        try {
          await deleteEventMutation.mutateAsync({
            accountId,
            calendarId: mapping.calendarId,
            eventId: mapping.eventId,
            userId: accountId,
          });
          await deleteMappingMutation.mutateAsync({
            accountId,
            taskId: mapping.taskId,
            userId: accountId,
          });
        } catch (error) {
          console.error('Error cleaning up orphaned event:', error);
        }
      }
      cleanupInProgressRef.current = false;
    };

    processOrphanedMappings();
  }, [mappingsQueries, accountIds, enabled, queryClient, deleteEventMutation, deleteMappingMutation]);
};